import PropTypes from "prop-types";
import {FormProvider, useForm} from "react-hook-form";
import styled from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {faHistory} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {InputTextArea} from "../../components/form/FormInputs.js";
import Modal from "../../components/Modal.js";

import {pad} from "../../style/components/variables.js";
import {
  Form,
  FormField,
  Inline,
  Button,
  HeadingCenter,
  Text,
  ModalButton
} from "../../style/components/general.js";

/**
 * Confirm a Component Service State change (in-service / out-of-service) for a
 * builder element (no dependency on history modal)
 * Used by ModalDisableElement and ModalElementDisabledHistory to avoid an import cycle.
 */
const ModalDisableElementForm = ({
  visible,
  setVisible,
  target,
  disabled,
  requireNote,
  submitNote,
  cancel,
  goBack,
  showHistory = false,
  onOpenHistory
}) => {
  const schema = yup.object().shape({
    note: yup
      .string()
      .nullable()
      .test({
        test: val => !requireNote || !!val,
        message: "Please provide explanation."
      })
  });

  const form = useForm({
    defaultValues: {
      note: ""
    },
    resolver: yupResolver(schema)
  });

  const {handleSubmit} = form;

  const submit = ({note}) => submitNote(note);

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton={!!goBack}
      goBack={goBack}
      renderModalControls={
        showHistory ? (
          <EventControls>
            <ModalButton onClick={onOpenHistory} title="History">
              <FontAwesomeIcon icon={faHistory} inverse />
            </ModalButton>
          </EventControls>
        ) : undefined
      }>
      <ModalTitle>
        Set{" "}
        {disabled ? target.enabledLabel || "In Service" : target.disabledLabel || "Out of Service"}
      </ModalTitle>
      <Text>
        Are you sure you sure you want to set the state of {target.label} to{" "}
        {disabled ? target.enabledLabel || "In Service" : target.disabledLabel || "Out of Service"}?
      </Text>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(submit)} noValidate>
          {requireNote && (
            <FormField>
              <InputTextArea
                name="note"
                label="Explanation"
                testId="addNote.message"
                maxLength={1000}
              />
            </FormField>
          )}

          <ButtonWrapper>
            <Button type="submit">Confirm</Button>
            <Button type="button" onClick={() => cancel()}>
              Cancel
            </Button>
          </ButtonWrapper>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalDisableElementForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  submitNote: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  cancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  target: PropTypes.objectOf(PropTypes.any).isRequired,
  requireNote: PropTypes.bool,
  showHistory: PropTypes.bool,
  onOpenHistory: PropTypes.func
};

const ModalTitle = styled(HeadingCenter)`
  margin-bottom: ${pad * 2}px;
`;

const ButtonWrapper = styled(Inline)`
  gap: ${pad}px;
  margin-top: ${pad}px;
`;

const EventControls = styled.div`
  display: flex;
  margin-right: ${pad}px;
`;

export default ModalDisableElementForm;
